import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { api } from "../../lib/api";
import { toast } from "sonner";
import ProductCard from "../../components/store/ProductCard";
import Filters from "../../components/store/Filters";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "../../components/ui/sheet";
import { SlidersHorizontal, X, Search, Share2 } from "lucide-react";

// Filter-state ↔ URL-param mapping
const MULTI_KEYS = ["color", "fabric", "occasion", "size", "work_type", "border_type", "fit", "sleeve_type", "sellers"];
const MULTI_PARAM = {
  color: "colors",
  fabric: "fabrics",
  occasion: "occasions",
  size: "sizes",
  work_type: "work_types",
  border_type: "border_types",
  fit: "fits",
  sleeve_type: "sleeve_types",
  sellers: "sellers",
};
const BOOL_KEYS = ["ready_to_ship", "blouse_included", "in_stock"];
const NUM_KEYS = ["min_price", "max_price", "discount_min", "min_rating"];
const SCALAR_KEYS = ["q"];

const SORT_OPTIONS = [
  { value: "new", label: "Newest" },
  { value: "popularity", label: "Popularity" },
  { value: "best_selling", label: "Best Selling" },
  { value: "rating", label: "Top Rated" },
  { value: "price_asc", label: "Price: Low to High" },
  { value: "price_desc", label: "Price: High to Low" },
  { value: "discount", label: "Biggest Discount" },
];

// URL <-> filter object helpers -------------------------------------------
function urlToFilters(sp) {
  const f = {};
  SCALAR_KEYS.forEach((k) => { const v = sp.get(k); if (v) f[k] = v; });
  NUM_KEYS.forEach((k) => { const v = sp.get(k); if (v !== null && v !== "") f[k] = Number(v); });
  BOOL_KEYS.forEach((k) => { if (sp.get(k) === "true") f[k] = true; });
  MULTI_KEYS.forEach((k) => {
    const v = sp.get(MULTI_PARAM[k]);
    if (v) f[k] = v.split(",").filter(Boolean);
  });
  return f;
}

function filtersToUrl(f, sort, page) {
  const params = new URLSearchParams();
  SCALAR_KEYS.forEach((k) => { if (f[k]) params.set(k, f[k]); });
  NUM_KEYS.forEach((k) => { if (f[k] !== undefined && f[k] !== "") params.set(k, String(f[k])); });
  BOOL_KEYS.forEach((k) => { if (f[k]) params.set(k, "true"); });
  MULTI_KEYS.forEach((k) => {
    if (Array.isArray(f[k]) && f[k].length) params.set(MULTI_PARAM[k], f[k].join(","));
  });
  if (sort && sort !== "new") params.set("sort", sort);
  if (page && page > 1) params.set("page", String(page));
  return params;
}

// Build axios params (category goes in the path for /:category route, so
// we only include it when there is no :category route param active)
function buildApiParams(f, sort, page, categoryFromRoute) {
  const p = { sort, page, limit: 24, include_facets: true };
  if (f.q) p.q = f.q;
  NUM_KEYS.forEach((k) => { if (f[k] !== undefined && f[k] !== "") p[k] = f[k]; });
  BOOL_KEYS.forEach((k) => { if (f[k]) p[k] = true; });
  MULTI_KEYS.forEach((k) => {
    if (Array.isArray(f[k]) && f[k].length) p[MULTI_PARAM[k]] = f[k].join(",");
  });
  const cat = categoryFromRoute || f.category;
  if (cat) p.category = cat;
  return p;
}

// Chip list for the active filters bar
function activeChips(f, sort, facets) {
  const chips = [];
  if (f.q) chips.push({ key: "q", label: `"${f.q}"` });
  if (f.min_price != null && f.min_price !== "") chips.push({ key: "min_price", label: `Min ₹${f.min_price}` });
  if (f.max_price != null && f.max_price !== "") chips.push({ key: "max_price", label: `Max ₹${f.max_price}` });
  if (f.discount_min) chips.push({ key: "discount_min", label: `${f.discount_min}% off+` });
  if (f.min_rating) chips.push({ key: "min_rating", label: `${f.min_rating}★ & up` });
  if (f.in_stock) chips.push({ key: "in_stock", label: "In stock" });
  if (f.ready_to_ship) chips.push({ key: "ready_to_ship", label: "Ready to ship" });
  MULTI_KEYS.forEach((k) => {
    if (Array.isArray(f[k])) {
      f[k].forEach((v) => {
        let label = v;
        if (k === "sellers") {
          const s = (facets?.sellers || []).find((x) => x.id === v);
          label = s?.shop_name || v;
        }
        chips.push({ key: k, value: v, label: `${label}` });
      });
    }
  });
  if (sort && sort !== "new") {
    const s = SORT_OPTIONS.find((o) => o.value === sort);
    if (s) chips.push({ key: "__sort", label: `Sort: ${s.label}` });
  }
  return chips;
}

export default function Listing() {
  const { category } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [total, setTotal] = useState(0);
  const [facets, setFacets] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [categories, setCategories] = useState([]);
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);

  // Hydrate from URL on mount / URL change (back/forward buttons)
  const filtersFromUrl = useMemo(() => urlToFilters(searchParams), [searchParams]);
  const sortFromUrl = searchParams.get("sort") || "new";
  // Guard against non-numeric ?page=abc values (Number("abc") → NaN)
  const pageFromUrl = Math.max(1, Number(searchParams.get("page")) || 1);

  const [filters, setFiltersState] = useState(filtersFromUrl);
  const [sort, setSortState] = useState(sortFromUrl);
  const [page, setPageState] = useState(pageFromUrl);
  const [searchInput, setSearchInput] = useState(filtersFromUrl.q || "");

  // Sync state when URL changes externally (e.g., back button, category route change)
  useEffect(() => {
    setFiltersState(filtersFromUrl);
    setSortState(sortFromUrl);
    setPageState(pageFromUrl);
    setSearchInput(filtersFromUrl.q || "");
  }, [filtersFromUrl, sortFromUrl, pageFromUrl]);

  // Push state → URL (replace so back button doesn't collect every filter toggle)
  const pushUrl = useCallback((f, s, p) => {
    const params = filtersToUrl(f, s, p);
    setSearchParams(params, { replace: true });
  }, [setSearchParams]);

  const setFilters = useCallback((updater) => {
    setFiltersState((cur) => {
      const next = typeof updater === "function" ? updater(cur) : updater;
      // Defer side-effects (router state + page reset) out of the updater body
      // to avoid React's "setState during render" warning in strict mode.
      queueMicrotask(() => {
        setPageState(1);
        pushUrl(next, sort, 1);
      });
      return next;
    });
  }, [sort, pushUrl]);

  const setSort = (s) => { setSortState(s); setPageState(1); pushUrl(filters, s, 1); };
  const setPage = (p) => { setPageState(p); pushUrl(filters, sort, p); window.scrollTo({ top: 0, behavior: "smooth" }); };

  // Debounced search input → filter
  useEffect(() => {
    const t = setTimeout(() => {
      if (searchInput !== (filters.q || "")) {
        setFilters((f) => ({ ...f, q: searchInput || undefined }));
      }
    }, 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchInput]);

  // Load flat categories for the sidebar quick-nav
  useEffect(() => {
    api.get("/categories").then(({ data }) => {
      setCategories((data || []).filter((c) => c.level === 1 || c.level === undefined));
    }).catch(() => setCategories([]));
  }, []);

  // Main products fetch
  const apiParams = useMemo(
    () => buildApiParams(filters, sort, page, category),
    [filters, sort, page, category]
  );

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setError("");
    api.get("/products", { params: apiParams })
      .then(({ data }) => {
        if (!alive) return;
        setProducts(data.items);
        setTotal(data.total);
        setFacets(data.facets || {});
      })
      .catch((e) => {
        if (!alive) return;
        setError(e?.response?.data?.detail || "Could not load products");
        setProducts([]);
        setTotal(0);
        setFacets({});
      })
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [apiParams]);

  const activeCat = categories.find((c) => c.slug === (category || filters.category));
  const chips = useMemo(() => activeChips(filters, sort, facets), [filters, sort, facets]);

  const removeChip = (chip) => {
    if (chip.key === "__sort") { setSort("new"); return; }
    if (MULTI_KEYS.includes(chip.key)) {
      setFilters((f) => {
        const next = (f[chip.key] || []).filter((v) => v !== chip.value);
        return { ...f, [chip.key]: next.length ? next : undefined };
      });
      return;
    }
    setFilters((f) => ({ ...f, [chip.key]: undefined }));
  };

  const clearAll = () => {
    setSearchInput("");
    setSortState("new");
    setPageState(1);
    setFiltersState({});
    pushUrl({}, "new", 1);
  };

  const shareLink = async () => {
    try {
      const href = window.location.href;
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(href);
      } else {
        const ta = document.createElement("textarea");
        ta.value = href;
        ta.style.position = "fixed"; ta.style.opacity = "0";
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
      }
      toast.success("Link copied to clipboard");
    } catch {
      toast.error("Could not copy link");
    }
  };

  const totalPages = Math.max(1, Math.ceil(total / 24));
  const showPagination = total > 24;

  const FilterPanel = (
    <Filters filters={filters} setFilters={setFilters} categories={categories} facets={facets} />
  );

  return (
    <div className="px-4 sm:px-6 md:px-12 lg:px-24 py-8 md:py-10" data-testid="listing-page">
      <div className="mb-6 md:mb-10">
        <div className="overline mb-2">Bazaar</div>
        <h1 className="font-display text-3xl sm:text-4xl lg:text-6xl">
          {activeCat ? activeCat.name : filters.q ? `"${filters.q}"` : "All Products"}
        </h1>
        <p className="text-[hsl(var(--kb-muted))] mt-2" data-testid="results-count">
          {loading ? "Loading…" : `${total} piece${total === 1 ? "" : "s"} curated`}
        </p>
      </div>

      {/* Search bar + mobile filter trigger + sort */}
      <div className="flex flex-wrap items-center gap-3 mb-4 pb-4 border-b border-[hsl(var(--kb-border))]">
        <div className="flex items-center gap-2 border border-[hsl(var(--kb-border))] px-3 py-2 flex-1 min-w-[200px] focus-within:border-[hsl(var(--kb-primary))]">
          <Search size={14} className="text-[hsl(var(--kb-muted))]" aria-hidden="true" />
          <input
            type="search"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Search title, SKU, brand, tags…"
            className="flex-1 bg-transparent text-sm focus:outline-none"
            data-testid="listing-search-input"
            aria-label="Search products"
            maxLength={100}
          />
          {searchInput && (
            <button
              onClick={() => setSearchInput("")}
              className="text-[hsl(var(--kb-muted))] hover:text-[hsl(var(--kb-primary))]"
              aria-label="Clear search"
              data-testid="listing-search-clear"
            >
              <X size={14} />
            </button>
          )}
        </div>

        <button
          onClick={() => setMobileFiltersOpen(true)}
          className="md:hidden flex items-center gap-2 border border-[hsl(var(--kb-border))] px-4 py-2 text-sm"
          data-testid="mobile-filters-btn"
          aria-label="Open filters"
        >
          <SlidersHorizontal size={14} /> Filters
          {chips.length > 0 && (
            <span className="bg-[hsl(var(--kb-primary))] text-white text-[10px] rounded-full px-2 py-0.5">
              {chips.length}
            </span>
          )}
        </button>

        <select
          value={sort}
          onChange={(e) => setSort(e.target.value)}
          className="bg-transparent text-sm border border-[hsl(var(--kb-border))] px-3 py-2 focus:outline-none focus:border-[hsl(var(--kb-primary))]"
          data-testid="sort-select"
          aria-label="Sort by"
        >
          {SORT_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
      </div>

      {/* Active filter chips */}
      {chips.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-6 items-center" data-testid="active-chips">
          {chips.map((c, i) => (
            <button
              key={`${c.key}-${c.value || i}`}
              onClick={() => removeChip(c)}
              className="inline-flex items-center gap-1 border border-[hsl(var(--kb-border))] hover:border-[hsl(var(--kb-primary))] px-3 py-1 text-xs"
              data-testid={`chip-${c.key}-${c.value || i}`}
            >
              {c.label}
              <X size={11} />
            </button>
          ))}
          <button
            onClick={clearAll}
            className="text-xs uppercase tracking-wider underline px-2"
            data-testid="chips-clear-all"
          >
            Clear all
          </button>
          <button
            onClick={shareLink}
            className="inline-flex items-center gap-1 text-xs uppercase tracking-wider px-3 py-1 border border-dashed border-[hsl(var(--kb-border))] hover:border-[hsl(var(--kb-primary))] hover:text-[hsl(var(--kb-primary))]"
            data-testid="share-filter-url"
            title="Copy link to this view"
            aria-label="Copy link to this view"
          >
            <Share2 size={12} /> Share
          </button>
        </div>
      )}

      <div className="grid md:grid-cols-[260px_1fr] gap-6 lg:gap-10">
        <aside className="hidden md:block" data-testid="desktop-filters">
          <div className="sticky top-[140px] max-h-[calc(100vh-160px)] overflow-y-auto pr-2">
            {FilterPanel}
          </div>
        </aside>

        <div>
          {error && (
            <div className="mb-6 border border-red-300 bg-red-50 text-red-800 p-3 text-sm" data-testid="listing-error">
              {error}
            </div>
          )}
          {loading && products.length === 0 ? (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 md:gap-8" data-testid="listing-loading">
              {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="aspect-[3/4] bg-[hsl(var(--kb-surface-alt))] animate-pulse" />
              ))}
            </div>
          ) : products.length === 0 ? (
            <div className="py-16 md:py-24 text-center text-[hsl(var(--kb-muted))]" data-testid="no-results">
              <p className="mb-4">No pieces match your filters.</p>
              <button className="kb-btn-outline" onClick={clearAll} data-testid="no-results-clear">
                Clear all filters
              </button>
            </div>
          ) : (
            <>
              <div
                className={`grid grid-cols-2 md:grid-cols-3 gap-4 md:gap-8 transition-opacity ${loading ? "opacity-60" : ""}`}
                aria-busy={loading}
              >
                {products.map((p) => <ProductCard key={p.id} product={p} />)}
              </div>
              {showPagination && (
                <div className="mt-10 flex items-center justify-center gap-2" data-testid="pagination">
                  <button
                    disabled={page <= 1}
                    onClick={() => setPage(page - 1)}
                    className="border border-[hsl(var(--kb-border))] px-4 py-2 text-sm disabled:opacity-40"
                    data-testid="page-prev"
                  >
                    Previous
                  </button>
                  <span className="text-sm px-3" data-testid="page-indicator">
                    Page {page} of {totalPages}
                  </span>
                  <button
                    disabled={page >= totalPages}
                    onClick={() => setPage(page + 1)}
                    className="border border-[hsl(var(--kb-border))] px-4 py-2 text-sm disabled:opacity-40"
                    data-testid="page-next"
                  >
                    Next
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Mobile filter drawer */}
      <Sheet open={mobileFiltersOpen} onOpenChange={setMobileFiltersOpen}>
        <SheetContent side="right" className="w-80 p-6 overflow-y-auto" data-testid="mobile-filters-drawer">
          <SheetHeader>
            <SheetTitle>Filters</SheetTitle>
            <SheetDescription className="sr-only">Refine the bazaar</SheetDescription>
          </SheetHeader>
          <div className="mt-4">
            {FilterPanel}
          </div>
          <button
            onClick={() => setMobileFiltersOpen(false)}
            className="kb-btn-primary w-full mt-6"
            data-testid="mobile-filters-apply"
          >
            Show {total} results
          </button>
        </SheetContent>
      </Sheet>
    </div>
  );
}
